import { useState, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import addressApi from "api/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { HugeiconsIcon } from "@hugeicons/react";
import { File02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Select } from "shared/components/forms/Select";
import { InputSize } from "shared/components/forms/Input";
import { usePrescreeningTemplatesController } from "views/listing-settings/controller";
import { Loader } from "shared/components/Loader";

interface ListingTemplateSectionProps {
  propertyId: number;
  prescreeningTemplateId?: string;
}

export const ListingTemplateSection = ({
  propertyId,
  prescreeningTemplateId
}: ListingTemplateSectionProps) => {
  const queryClient = useQueryClient();
  const { templates, isFetching } = usePrescreeningTemplatesController();
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    prescreeningTemplateId
  );

  const { mutate: editListingTemplate, isPending } = useMutation({
    mutationKey: ["PROPERTIES", propertyId, "UPDATE_LISTING_TEMPLATE"],
    mutationFn: (templateIdentifier: string) => {
      return addressApi.editProperty({
        propertyId,
        prescreening_template_identifier: templateIdentifier
      });
    },
    onSuccess: () => {
      toast.success("Listing template updated");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
    },
    onError: (error) => {
      toast.error("Failed to update listing template", getErrorMessage(error));
    }
  });

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    editListingTemplate(templateId);
  };

  const listingTemplateOptions = useMemo(() => {
    if (!templates) return [];

    return templates
      .map((template) => ({
        value: template.identifier,
        label: template.name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [templates]);

  const defaultTemplate = templates?.find((t) => t.default);

  const templateSelectValue = useMemo(() => {
    if (selectedTemplateId) return selectedTemplateId;
    if (defaultTemplate) return defaultTemplate.identifier;
    return "";
  }, [selectedTemplateId, defaultTemplate]);

  if (isFetching)
    return (
      <SectionCard title="Listing template">
        <div className="flex items-center justify-center w-[700px]">
          <Loader />
        </div>
      </SectionCard>
    );

  return (
    <SectionCard title="Listing template" noPadding>
      <div className="py-7 px-8 flex items-center gap-4">
        <div className="flex-shrink-0 w-10 h-10 bg-primary-20 rounded-lg flex items-center justify-center">
          <HugeiconsIcon
            icon={File02Icon}
            className="size-5 text-primary-500"
          />
        </div>
        <h3 className="flex-1 text-base font-medium text-content-1000">
          Selected template
        </h3>
        <Select
          id="listing-template-select"
          value={templateSelectValue}
          onChange={(e) => handleTemplateSelect(e.target.value)}
          isDisabled={isPending || isFetching}
          options={listingTemplateOptions}
          className="w-60"
          legacyErrorMargins={false}
          size={InputSize.SM}
          searchable
        />
      </div>
    </SectionCard>
  );
};
